import ControlPanelController from "./control_panel_controller"

const KEBAB_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>'

export default class extends ControlPanelController {
  static targets = [
    "list", "createModal", "createName",
    "memberModal", "memberGroupId", "memberGroupType", "memberEmail", "memberRole",
    "renameModal", "renameId", "renameType", "renameName",
    "subgroupModal", "subgroupPortfolioId", "subgroupName",
    "deleteConfirmModal", "deleteId", "deleteType"
  ]

  static values = {
    listUrl: String,
    createUrl: String,
    baseUrl: String,
    programsBaseUrl: String
  }

  connect() {
    super.connect()
    this._onClickOutside = this._closeAllMenus.bind(this)
    document.addEventListener("click", this._onClickOutside)
    this.loadPortfolios()
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
  }

  async loadPortfolios() {
    try {
      const response = await fetch(this.listUrlValue, { headers: { Accept: "application/json" } })
      const portfolios = await response.json()
      this._renderPortfolios(portfolios)
    } catch {
      this.listTarget.innerHTML = '<div class="text-center py-8 text-red-400 text-[13px]">Error loading categories</div>'
    }
  }

  _renderPortfolios(portfolios) {
    if (!portfolios || portfolios.length === 0) {
      this.listTarget.innerHTML = '<div class="text-center py-8 text-gray-400 text-[13px]">No categories found</div>'
      return
    }

    this.listTarget.innerHTML = `
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-[13px]">
          ${portfolios.map(p => `
            <tbody>
              <tr class="bg-gray-50 border-b border-gray-200">
                <td class="px-3 py-1.5 text-[13px] font-semibold text-gray-500">${this._esc(p.name)}</td>
                <td class="px-3 py-1.5 text-[13px] text-text-muted text-right whitespace-nowrap">${(p.programs || []).length} subcategories</td>
                <td class="px-3 py-1.5 text-right w-10">
                  <div class="relative">
                    <button type="button" class="p-1 rounded hover:bg-gray-200 cursor-pointer"
                            data-action="click->cpanel-groups#toggleMenu">
                      ${KEBAB_SVG}
                    </button>
                    <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40">
                      <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                              data-action="click->cpanel-groups#openSubgroupModal"
                              data-id="${p.id}">Add Subcategory</button>
                      <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                              data-action="click->cpanel-groups#openRenameModal"
                              data-id="${p.id}" data-type="portfolio" data-name="${this._esc(p.name)}">Rename</button>
                      <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                              data-action="click->cpanel-groups#deleteGroup"
                              data-id="${p.id}">Remove</button>
                    </div>
                  </div>
                </td>
              </tr>
              ${(p.programs || []).map(prog => `
                <tr class="bg-white hover:bg-gray-50 transition-colors border-b border-gray-200">
                  <td class="px-3 py-1.5 text-[13px] font-medium text-gray-500 pl-8">${this._esc(prog.name)}</td>
                  <td></td>
                  <td class="px-3 py-1.5 text-right w-10">
                    <div class="relative">
                      <button type="button" class="p-1 rounded hover:bg-gray-200 cursor-pointer"
                              data-action="click->cpanel-groups#toggleMenu">
                        ${KEBAB_SVG}
                      </button>
                      <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40">
                        <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                                data-action="click->cpanel-groups#openRenameModal"
                                data-id="${prog.id}" data-type="program" data-name="${this._esc(prog.name)}">Rename</button>
                        <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                                data-action="click->cpanel-groups#deleteProgram"
                                data-id="${prog.id}">Remove</button>
                      </div>
                    </div>
                  </td>
                </tr>
              `).join("")}
            </tbody>
          `).join("")}
        </table>
      </div>`
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  // ── Kebab menus ───────────────────────────────────────────────────

  toggleMenu(event) {
    event.stopPropagation()
    const menu = event.currentTarget.nextElementSibling
    const wasHidden = menu.classList.contains("hidden")
    this._closeAllMenus()
    if (wasHidden) menu.classList.remove("hidden")
  }

  _closeAllMenus() {
    this.listTarget.querySelectorAll(".relative > div.absolute").forEach(m => m.classList.add("hidden"))
  }

  // ── Create Portfolio ──────────────────────────────────────────────

  async submitCreate() {
    const name = this.createNameTarget.value.trim()
    if (!name) return

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ portfolio: { name } })
      })
      if (response.ok) {
        this.closeCreateModal()
        this._showToast("Category created")
        this.loadPortfolios()
      } else {
        const data = await response.json().catch(() => ({}))
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Add Subgroup ──────────────────────────────────────────────────

  openSubgroupModal(event) {
    this._closeAllMenus()
    this.subgroupPortfolioIdTarget.value = event.currentTarget.dataset.id
    this.subgroupNameTarget.value = ""
    this.subgroupModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeSubgroupModal() {
    this.subgroupModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  async submitSubgroup() {
    const name = this.subgroupNameTarget.value.trim()
    const portfolioId = this.subgroupPortfolioIdTarget.value
    if (!name) return

    try {
      const response = await fetch(this.programsBaseUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ program: { name, portfolio_id: portfolioId } })
      })
      if (response.ok) {
        this.closeSubgroupModal()
        this._showToast("Subcategory created")
        this.loadPortfolios()
      } else {
        const data = await response.json().catch(() => ({}))
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Rename ────────────────────────────────────────────────────────

  openRenameModal(event) {
    this._closeAllMenus()
    this.renameIdTarget.value = event.currentTarget.dataset.id
    this.renameTypeTarget.value = event.currentTarget.dataset.type
    this.renameNameTarget.value = event.currentTarget.dataset.name
    this.renameModalTarget.classList.remove("hidden")
    this._lockScroll()
    this.renameNameTarget.focus()
  }

  closeRenameModal() {
    this.renameModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  async submitRename() {
    const name = this.renameNameTarget.value.trim()
    const id = this.renameIdTarget.value
    const type = this.renameTypeTarget.value
    if (!name) return

    const baseUrl = type === "program" ? this.programsBaseUrlValue : this.baseUrlValue
    const bodyKey = type === "program" ? "program" : "portfolio"

    try {
      const response = await fetch(`${baseUrl}/${id}`, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify({ [bodyKey]: { name } })
      })
      if (response.ok) {
        this.closeRenameModal()
        this._showToast("Renamed")
        this.loadPortfolios()
      } else {
        const data = await response.json().catch(() => ({}))
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Delete ────────────────────────────────────────────────────────

  deleteGroup(event) {
    this._closeAllMenus()
    this.deleteIdTarget.value = event.currentTarget.dataset.id
    this.deleteTypeTarget.value = "portfolio"
    this.deleteConfirmModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  deleteProgram(event) {
    this._closeAllMenus()
    this.deleteIdTarget.value = event.currentTarget.dataset.id
    this.deleteTypeTarget.value = "program"
    this.deleteConfirmModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeDeleteConfirm() {
    this.deleteConfirmModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  async submitDelete() {
    const id = this.deleteIdTarget.value
    const type = this.deleteTypeTarget.value
    const baseUrl = type === "program" ? this.programsBaseUrlValue : this.baseUrlValue

    try {
      const response = await fetch(`${baseUrl}/${id}`, {
        method: "DELETE",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      this.closeDeleteConfirm()
      this._showToast(data.message || "Deleted")
      this.loadPortfolios()
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Members ───────────────────────────────────────────────────────

  openAddMember(event) {
    this._closeAllMenus()
    this.memberGroupIdTarget.value = event.currentTarget.dataset.id
    this.memberGroupTypeTarget.value = event.currentTarget.dataset.type || "portfolio"
    this.memberModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeMemberModal() {
    this.memberModalTarget.classList.add("hidden")
    this._unlockScroll()
    this.memberEmailTarget.value = ""
    this.memberRoleTarget.value = "member"
  }

  async submitAddMember() {
    const id = this.memberGroupIdTarget.value
    const type = this.memberGroupTypeTarget.value
    const email = this.memberEmailTarget.value.trim()
    const role = this.memberRoleTarget.value

    if (!email) return

    const baseUrl = type === "program" ? this.programsBaseUrlValue : this.baseUrlValue
    try {
      const response = await fetch(`${baseUrl}/${id}/members`, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ email, role })
      })
      const data = await response.json().catch(() => ({}))
      this.closeMemberModal()
      this._showToast(data.message || "Member added")
      this.loadPortfolios()
    } catch {
      this._showToast("Network error", "error")
    }
  }
}
